import {
  Alert,
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  Label,
  Modal,
  Paragraph,
  Select,
  Spinner,
  Tag,
  Textarea,
  TextButton,
  Toast,
  Tooltip,
  UnstyledButton,
  useToast,
} from '@zillow/constellation-10';
import { IconDeleteOutline, IconDollarSignCircleFilled, IconPlusOutline } from '@zillow/constellation-icons';
import { useMount } from 'ahooks';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import { ApplicationRole, IncomeType } from '@zg-rentals/ts-capps-client';
import type { Answers, Document, DocumentInput } from '../../../../models/rental-application';
import { validateDate, validateEmail } from '../../../../utils/validation';
import { MAX_DOCUMENTS_PER_INCOME, TEXT_AREA_MAX_LENGTH_510 } from '../../../../constants/form';
import { date50YearsAgo, isValidUSPhoneNumber } from '../../../../utils/formatters';
import type { ZDocsUpload } from '@zillow/zdocs-web';
import { ZDocsUploader } from '@zillow/zdocs-web';
import {
  useCappsRenterDocumentsWebApiHandler_addIncomeDocuments,
  useCappsRenterDocumentsWebApiHandler_deleteDocument,
  useCappsRenterDocumentsWebApiHandler_getEditorUrl,
  useCappsRenterDocumentsWebApiHandler_requestUserToken,
} from '@zg-rentals/ts-capps-client-react-query';
import { DOCUMENTS_EXPIRED_TEXT, INCOME_DOC_ENCOURAGEMENT_TEXT } from '../../../../constants/incomeDocs';
import { MaskedCurrencyInput, MaskedPhoneInput } from '../../../Inputs';
import { css } from '@/styled-system/css';
import type React from 'react';
import { useState } from 'react';
import { emitAddMoreIncomeEvent, emitNextReviewIncomeSourcesEvent } from '../../../../analytics/clickstreamEvents';
import { DatePicker } from '../../../DatePicker';
import { useIncomeDocsRedaction } from '../../../../hooks/useIncomeDocsRedaction';
import { toDocument } from '../../../../hooks/useRetrieveActiveDocuments';
import { validateCurrency } from '../../../Inputs/MaskedCurrencyInput';
import { useApplicationContextOrUndefined } from '../../../../contexts/ApplicationContext';
import { RENTAL_APPLICATION_TAG } from '../../../../hooks/useRentalApplication';
import type { ButtonRootPropsInterface } from '@zillow/constellation-10';
import { GuarantorSection } from './GuarantorSection';

const emptyCurrentIncome = {
  type: IncomeType.EMPLOYMENT,
  startDate: '',
  employer: { organization: '', name: '', email: '', phone: '' },
  amount: '',
  title: '',
  documents: [],
  note: '',
};

export function CurrentIncomeStep({
  applicationId,
  onPersist,
}: {
  applicationId: string;
  onPersist: () => Promise<void>;
}) {
  // Consider that useFieldArray substitutes ids for a random uuid, so item.id is
  // not stable and not the same as the backend id, see https://github.com/orgs/react-hook-form/discussions/8935
  // This is tracked in RA-6890
  const { fields, append, remove } = useFieldArray({ name: 'currentIncomes' });
  const applicationContext = useApplicationContextOrUndefined();
  const canAddGuarantor =
    applicationContext?.featureFlags.isGuarantorInvitationsEnabled === true &&
    applicationContext.application.role !== ApplicationRole.GUARANTOR;

  useMount(() => {
    emitNextReviewIncomeSourcesEvent(applicationContext);
  });

  useMount(() => {
    if (fields.length === 0) {
      onAddRow();
    }
  });

  function onDeleteRow(index: number) {
    remove(index);
  }

  function onAddRow() {
    append(emptyCurrentIncome);
    onPersist();
  }

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        Current income
      </Heading>

      <Alert css={{ marginBottom: 'default' }}>
        Avoid sharing Social Security number, date of birth, account numbers, or other personal information
      </Alert>

      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        {fields.map((currentIncome, index) => (
          <CurrentIncomeEdit key={currentIncome.id} index={index} onDeleteRow={() => onDeleteRow(index)} />
        ))}
        <TextButton
          onClick={() => {
            emitAddMoreIncomeEvent(applicationContext);
            onAddRow();
          }}
          icon={<IconPlusOutline />}
          type="button"
        >
          Add income
        </TextButton>
        {canAddGuarantor ? <GuarantorSection applicationId={applicationId} /> : null}
      </Box>
    </>
  );
}

export function CurrentIncomeStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconDollarSignCircleFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
            Income sources
          </Heading>
          <Paragraph>
            Providing details about your income sources allows rental managers to verify your income and employment.
          </Paragraph>
        </>
      }
    />
  );
}

function CurrentIncomeEdit({ index, onDeleteRow }: { index: number; onDeleteRow: () => void }) {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext();

  const incomeType = watch(`currentIncomes[${index}].type`);

  const errorAmount = get(errors, `currentIncomes[${index}].amount`);
  const errorTitle = get(errors, `currentIncomes[${index}].title`);
  return (
    <>
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Heading level={3} textStyle="heading-xs" css={{ color: 'text.subtle' }}>
          Income source #{index + 1}
        </Heading>
        <TextButton onClick={onDeleteRow} type="button">
          Delete
        </TextButton>
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        <Controller
          name={`currentIncomes[${index}].type`}
          render={({ field: { onChange, value, onBlur } }) => (
            <FormField
              label={<Label>Type</Label>}
              control={
                <Select
                  value={value || ''}
                  onBlur={onBlur}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    if (e.target.value !== IncomeType.EMPLOYMENT) {
                      [
                        `currentIncomes[${index}].employer.organization`,
                        `currentIncomes[${index}].startDate`,
                        `currentIncomes[${index}].employer.name`,
                        `currentIncomes[${index}].employer.email`,
                        `currentIncomes[${index}].employer.phone`,
                      ].forEach((fieldToClear) => setValue(fieldToClear, null));
                    }
                    onChange(e.target.value);
                  }}
                >
                  <option value={IncomeType.EMPLOYMENT}>Employment</option>
                  <option value={IncomeType.CONTRACTOR_SELF_EMPLOYED}>Contract or self-employment</option>
                  <option value={IncomeType.HOUSING_VOUCHER}>Tenant-based voucher</option>
                  <option value={IncomeType.OTHER}>Other (specify)</option>
                </Select>
              }
            />
          )}
        />
        <Controller
          name={`currentIncomes[${index}].amount`}
          rules={{ required: 'Income amount is required', validate: validateCurrency }}
          render={({ field }) => (
            <FormField
              label={
                <Label id={`currentIncomes${index}-amount-label`}>
                  Monthly {incomeType === IncomeType.HOUSING_VOUCHER ? 'approved amount' : 'income before taxes'}
                </Label>
              }
              control={<MaskedCurrencyInput {...field} aria-labelledby={`currentIncomes${index}-amount-label`} />}
              description={
                errorAmount?.message ? (
                  <FormHelp>{String(errorAmount.message)}</FormHelp>
                ) : incomeType === IncomeType.HOUSING_VOUCHER ? (
                  <FormHelp>Enter the voucher payment standard for your approved unit size in your area</FormHelp>
                ) : undefined
              }
              error={!!errorAmount}
            />
          )}
        />
        <FormField
          label={<Label>{getIncomeTitle(incomeType)}</Label>}
          control={
            <Input
              maxLength={32}
              {...register(`currentIncomes[${index}].title`, { required: 'This field is required' })}
            />
          }
          description={errorTitle?.message ? <FormHelp>{String(errorTitle.message)}</FormHelp> : undefined}
          error={!!errorTitle}
        />
        <IncomeDocs index={index} incomeType={incomeType} />
        {incomeType === IncomeType.EMPLOYMENT && <EmployerContact index={index} />}
        <FormField
          label={<Label>Additional information</Label>}
          control={
            <Textarea
              maxLength={TEXT_AREA_MAX_LENGTH_510}
              {...register(`currentIncomes[${index}].note`)}
              css={{ resize: 'vertical' }}
            />
          }
          optional
        />
      </Box>
    </>
  );
}

export function getIncomeTitle(incomeType: IncomeType) {
  return incomeType === IncomeType.EMPLOYMENT
    ? 'Job title'
    : incomeType === IncomeType.HOUSING_VOUCHER
      ? 'Issuing agency'
      : 'Income source description';
}

function IncomeDocs({ index, incomeType }: { index: number; incomeType: IncomeType }) {
  const { watch } = useFormContext();
  const formDocuments: Array<Document> = watch(`currentIncomes[${index}].documents`) || [];
  const { documents: activeDocuments, areDocumentsExpired } = useIncomeDocsRedaction(formDocuments);

  const documents = formDocuments.map((doc) => activeDocuments?.find((x) => x.id === doc.id) ?? doc);
  return (
    <>
      <IncomeDocsHeader incomeType={incomeType} />
      {areDocumentsExpired && <Alert tone="warning">{DOCUMENTS_EXPIRED_TEXT}</Alert>}
      <IncomeDocsPrivacyProtection />
      <UploadedIncomeDocs index={index} documents={documents} />
      <IncomeDocsUploader index={index} />
    </>
  );
}

function IncomeDocsHeader({ incomeType }: { incomeType: IncomeType }) {
  return (
    <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
      <Paragraph textStyle="body-sm-bold">Income documents</Paragraph>
      <Tooltip content={INCOME_DOC_ENCOURAGEMENT_TEXT[incomeType]} />
    </Box>
  );
}

function UploadedIncomeDocs({ index, documents }: { index: number; documents: Array<Document> }) {
  return (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
      {documents.map((document: Document, i) => (
        <IncomeDocPreview key={`${i}_${document.id}`} index={index} document={document} />
      ))}
    </Box>
  );
}

function getIsRedactionComplete(document: Document) {
  if (!document.isRedactionCheckComplete) {
    return false;
  }

  if (document.isRedacted) {
    return true;
  }

  if (document.canBeRedacted) {
    return false;
  }

  return true;
}

function DeleteDocumentButton({
  documentId,
  incomeSourceIndex,
  onCompleted,
  children,
  ...buttonProps
}: {
  documentId: string;
  incomeSourceIndex: number;
  onCompleted?: () => void;
} & React.PropsWithChildren<Omit<ButtonRootPropsInterface, 'onClick'>>) {
  const { setValue, getValues } = useFormContext();

  const { mutate: deleteDocument, isPending } = useCappsRenterDocumentsWebApiHandler_deleteDocument({
    meta: {
      invalidates: [RENTAL_APPLICATION_TAG],
    },
    onSuccess: () => {
      const documents = getValues(`currentIncomes[${incomeSourceIndex}].documents`) as Array<Document>;
      setValue(
        `currentIncomes[${incomeSourceIndex}].documents`,
        documents.filter((d) => d.id !== documentId),
      );
      onCompleted?.();
    },
  });

  const onClick = () => {
    if (!documentId) {
      return;
    }

    deleteDocument({ postData: { documentId } });
  };

  return (
    <Button {...buttonProps} onClick={onClick} loading={isPending}>
      {children}
    </Button>
  );
}

function IncomeDocPreview({ index, document }: { index: number; document: Document }) {
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isExpired = document.expirationDateTime && new Date(document.expirationDateTime).valueOf() < Date.now();
  const isRedactionComplete = getIsRedactionComplete(document);
  const isDocumentReadyToView = !!document.id && isRedactionComplete && !isExpired;
  const {
    data: editorUrlData,
    isError: isEditorUrlError,
    isFetching: isFetchingEditorUrl,
  } = useCappsRenterDocumentsWebApiHandler_getEditorUrl(
    { documentId: document.id! },
    {
      enabled: isPreviewOpen && isDocumentReadyToView,
      meta: {
        tags: ['cappsRenterDocuments', 'getEditorUrl'],
        context: { api: 'cappsRenterDocuments', details: 'getEditorUrl' },
      },
    },
  );
  const editorUrl = editorUrlData?.documentEditorUrl;

  return (
    <>
      <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 6', lg: 'span 4' } }}>
        <Card
          elevated={false}
          tone="soft"
          body={
            <>
              {isDocumentReadyToView ? (
                <UnstyledButton
                  type="button"
                  onClick={() => setIsPreviewOpen(true)}
                  aria-label={`Preview ${document.fileName || 'income document'}`}
                  css={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                >
                  <Image
                    css={{ height: '96px', objectFit: 'contain' }}
                    src={document.previewUrl as string}
                    alt={`Preview image for ${document.fileName}`}
                  />
                </UnstyledButton>
              ) : (
                <Box css={{ height: '96px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isExpired ? <Tag tone="warning">Expired</Tag> : <Spinner />}
                </Box>
              )}
              <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Paragraph
                  textStyle="fineprint-bold"
                  css={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                >
                  {document?.fileName || `Income document`}
                </Paragraph>
                <IconButton
                  disabled={document.id == null}
                  icon={<IconDeleteOutline />}
                  type="button"
                  emphasis="bare"
                  size="xs"
                  title="Delete document"
                  onClick={() => setIsDeletingDoc(true)}
                />
              </Box>
            </>
          }
        />
      </Box>
      {isPreviewOpen && (
        <Modal.Root open size="fullScreen" onOpenChange={(open) => !open && setIsPreviewOpen(false)}>
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content>
                <Modal.Header>
                  <Heading level={2}>{document.fileName || 'Income document'}</Heading>
                </Modal.Header>
                <Divider />
                <Modal.Body css={{ padding: '0 !important' }}>
                  {isEditorUrlError && !isFetchingEditorUrl ? (
                    <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                      <Paragraph>We couldn’t load this document. Please close this window and try again.</Paragraph>
                    </Box>
                  ) : editorUrl ? (
                    <iframe
                      src={editorUrl}
                      title={`Preview of ${document.fileName || 'income document'}`}
                      className={css({ width: '100%', height: '100%', border: 'none', display: 'block' })}
                    />
                  ) : (
                    <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                      <Spinner />
                    </Box>
                  )}
                </Modal.Body>
                {editorUrl && (
                  <>
                    <Divider />
                    <Modal.Footer>
                      <Anchor href={editorUrl} target="_blank" rel="noopener noreferrer">
                        Open in new tab
                      </Anchor>
                    </Modal.Footer>
                  </>
                )}
                <Modal.CloseButton />
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      )}
      {isDeletingDoc && (
        <Modal
          open
          onOpenChange={(open) => !open && setIsDeletingDoc(false)}
          header={<Heading level={2}>Document deletion</Heading>}
          body={
            <Paragraph>
              Are you sure you want to delete{' '}
              {document?.fileName ? <strong>{document?.fileName}</strong> : `this income document`}?
            </Paragraph>
          }
          footer={
            <ButtonGroup aria-label="Delete income document actions">
              <TextButton onClick={() => setIsDeletingDoc(false)} type="button">
                Cancel
              </TextButton>
              <DeleteDocumentButton
                documentId={document.id!}
                incomeSourceIndex={index}
                tone="critical"
                type="button"
                onCompleted={() => setIsDeletingDoc(false)}
              >
                Yes, delete
              </DeleteDocumentButton>
            </ButtonGroup>
          }
        />
      )}
    </>
  );
}

function IncomeDocsPrivacyProtection() {
  return (
    <Paragraph textStyle="body-sm" color="text.subtle">
      Sensitive information, like your SSN, may be automatically redacted when you upload a document. Preview your
      documents to verify that they do not include sensitive information.
    </Paragraph>
  );
}

function IncomeDocsUploader({ index }: { index: number }) {
  const { setValue, getValues, watch } = useFormContext();
  const { enqueueToast } = useToast();
  const documentsPath = `currentIncomes[${index}].documents`;
  const docsLength = (watch(documentsPath) as Array<DocumentInput>)?.length ?? 0;

  const MULTI_FILE_UPLOAD_ACCEPTED_FILES = ['.pdf', '.jpeg', '.jpg', '.png'];
  const PLACEHOLDER_TEXT = `Add up to ${MAX_DOCUMENTS_PER_INCOME} documents per income source. Password-protected and encrypted documents are not supported. Each file must be smaller than 100MB.`;
  const { mutateAsync: requestUserToken } = useCappsRenterDocumentsWebApiHandler_requestUserToken({
    meta: { context: { api: 'cappsRenterDocuments', details: 'requestUserToken' } },
  });
  const generateUploadToken = async () => {
    const result = await requestUserToken();
    return result.userToken ?? '';
  };

  const { mutateAsync: addIncomeDocuments } = useCappsRenterDocumentsWebApiHandler_addIncomeDocuments({
    meta: {
      invalidates: [RENTAL_APPLICATION_TAG],
    },
  });

  async function onUploadComplete(upload: ZDocsUpload) {
    const { messageToken } = upload;
    if (!messageToken) {
      enqueueToast(<Toast tone="critical">We couldn’t upload this document. Please try again.</Toast>);
      return;
    }

    // Temporarily add the "pending" document just to include it in the document length counter and in the preview,
    // otherwise we get a race condition where the document length is off by one during the "addIncomeDocuments" duration
    setValue(documentsPath, [
      // We should use "getValues" instead of documents otherwise we'll capture the document array value at the time of the function call
      ...getValues(documentsPath),
      {
        messageToken,
        // We add the fileName here for the preview
        fileName: upload.file.name.replace(/\.[^.]+$/, ''),
      },
    ]);

    let newDocs;
    try {
      newDocs = await addIncomeDocuments({
        postData: {
          incomeId: getValues(`currentIncomes[${index}].id`),
          documents: [{ messageToken }],
        },
      });
    } catch {
      // Drop the pending document added above, otherwise it stays stuck on a spinner
      // and keeps counting against MAX_DOCUMENTS_PER_INCOME
      setValue(
        documentsPath,
        (getValues(documentsPath) as Array<DocumentInput>).filter((d) => d.messageToken !== messageToken),
      );
      enqueueToast(<Toast tone="critical">We couldn’t save this document. Please try again.</Toast>);
      return;
    }

    setValue(
      documentsPath,
      // Since the mutation takes different time each time, this
      // gets executed out of order, so we substitute the item added above
      // to preserve the original order, we use flatMap since newDocs is an array,
      // even when we expect 1 document on the result array
      (getValues(documentsPath) as Array<DocumentInput>).flatMap((d) =>
        d.messageToken == messageToken ? (newDocs ?? []).map(toDocument) : [d],
      ),
    );
  }

  function onUploadFailed(failed: ZDocsUpload) {
    enqueueToast(<Toast tone="critical">{failed.errorMessage}</Toast>);
  }

  const maxFiles = MAX_DOCUMENTS_PER_INCOME - docsLength;

  const [zDocsReset, setZDocsReset] = useState(1);
  return (
    maxFiles > 0 && (
      <ZDocsUploader
        key={zDocsReset}
        generateUploadToken={generateUploadToken}
        onUploadComplete={onUploadComplete}
        onUploadActive={(isUploadActive) => {
          if (!isUploadActive) {
            // Reset the ZDocs components when all uploads are complete
            setZDocsReset((x) => 1 - x);
          }
        }}
        onUploadFailed={onUploadFailed}
        placeholderText={PLACEHOLDER_TEXT}
        accept={MULTI_FILE_UPLOAD_ACCEPTED_FILES}
        autoUpload
        maxFiles={maxFiles}
      />
    )
  );
}

function EmployerContact({ index }: { index: number }) {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const errorOrganization = get(errors, `currentIncomes[${index}].employer.organization`);
  const errorName = get(errors, `currentIncomes[${index}].employer.name`);
  const errorEmail = get(errors, `currentIncomes[${index}].employer.email`);
  const errorPhone = get(errors, `currentIncomes[${index}].employer.phone`);

  return (
    <>
      <FormField
        label={<Label>Employer</Label>}
        control={
          <Input
            maxLength={32}
            {...register(`currentIncomes[${index}].employer.organization`, { required: 'An employer is required' })}
          />
        }
        description={errorOrganization?.message ? <FormHelp>{String(errorOrganization.message)}</FormHelp> : undefined}
        error={!!errorOrganization}
      />
      <Controller
        name={`currentIncomes[${index}].startDate`}
        rules={{ required: 'A started-on date is required', validate: (v) => validateDate(v, { maxYearsAgo: 50 }) }}
        render={({ field, fieldState: { error, invalid } }) => (
          <FormField
            label={<Label id={`currentIncomes${index}-startDate-label`}>Started on</Label>}
            description={error?.message ? <FormHelp>{error.message}</FormHelp> : undefined}
            error={invalid}
          >
            <DatePicker
              {...field}
              minDate={date50YearsAgo()}
              aria-labelledby={`currentIncomes${index}-startDate-label`}
            />
          </FormField>
        )}
      />
      <FormField
        label={<Label>Employer contact name</Label>}
        control={
          <Input
            maxLength={100}
            {...register(`currentIncomes[${index}].employer.name`, { required: 'A name is required' })}
          />
        }
        description={
          errorName?.message ? (
            <FormHelp>{String(errorName.message)}</FormHelp>
          ) : (
            <FormHelp>
              Add the name of your manager or an HR representative who can verify your employment at this company
            </FormHelp>
          )
        }
        error={!!errorName}
      />
      <FormField
        label={<Label>Employer contact email</Label>}
        control={
          <Input
            maxLength={100}
            {...register(`currentIncomes[${index}].employer.email`, { validate: validateEmail })}
            type="email"
          />
        }
        description={errorEmail?.message ? <FormHelp>{String(errorEmail.message)}</FormHelp> : undefined}
        error={!!errorEmail}
        optional
      />
      <FormField
        label={<Label id={`currentIncomes${index}-employer-phone-label`}>Employer contact phone</Label>}
        control={
          <Controller
            name={`currentIncomes[${index}].employer.phone`}
            rules={{
              validate: (phone) => {
                if (!phone) return true;
                return isValidUSPhoneNumber(phone) || 'Enter a valid phone number';
              },
            }}
            render={({ field }) => (
              <MaskedPhoneInput {...field} aria-labelledby={`currentIncomes${index}-employer-phone-label`} />
            )}
          />
        }
        description={errorPhone?.message ? <FormHelp>{String(errorPhone.message)}</FormHelp> : undefined}
        error={!!errorPhone}
        optional
      />
    </>
  );
}

export const CurrentIncomeRecommendation = {
  condition: ({ answers }: { answers: Answers }) => {
    const currentIncomes = answers?.currentIncomes || [];
    return (
      currentIncomes.length > 0 && !currentIncomes.some((currentIncome) => (currentIncome?.documents || []).length > 0)
    );
  },
  body: (
    <Paragraph>
      More than 60% of applicants include income documents, which can help the rental manager make a faster decision
      about your application. Consider adding current income documents, such as pay stubs or W-2s, to your application.
    </Paragraph>
  ),
  title: 'Income documents',
};
